import { Plus, Users, UserX, Loader2 } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { CustomerSearchInputComponent } from '@/modules/shared/components/client-search-input.component'
import { FabComponent } from '@/modules/shared/components/fab.component'
import { CustomerCardComponent } from '../components/customer-card.component'
import { MENU_ACTIONS } from '../constants/menu-actions.constant'
import { BottomMenuComponent } from '@/modules/shared/components/bottom-menu.component'
import { CatalogoLinkDialogComponent } from '../components/catalogo-link-dialog.component'
import { toast } from 'sonner'
import { useAuthStore } from '@/modules/shared/stores/auth.store'
import type { useCustomerSearch } from '../hooks/use-customer-search.hook'

type Props = ReturnType<typeof useCustomerSearch>

export function CustomerSearchInterface({
	handleSearchClient,
	customers,
	clientSelected,
	handleSelectClientToStartActions,
	isDisabledDelete,
	handleStartViewClient,
	isDisabledEdit,
	handleStartAddClient,
	selectFunctionToActions,
	isLoading = false,
	isLoadingMore = false,
	hasMore = false,
	totalRecords = 0,
	loadMoreCustomers,
	catalogoDialogOpen,
	handleCloseCatalogoDialog,
}: Props) {
	const { versionUrl } = useAuthStore()
	const isApiV3 = versionUrl.includes('/v3')
	const loadingRef = useRef<HTMLDivElement>(null)

	// Obtém o companyCode do localStorage para controlar acesso ao catálogo
	const getCompanyCode = (): number => {
		try {
			const companyCode = localStorage.getItem('companyCode')
			return companyCode ? Number.parseInt(companyCode) : 1
		} catch (error) {
			console.error('Erro ao obter companyCode:', error)
			return 1
		}
	}

	const empresaId = getCompanyCode()
	const allowedCompanies = [89, 996] // Empresas autorizadas para o catálogo
	const showCatalogoLink = allowedCompanies.includes(empresaId)

	// Intersection Observer para scroll infinito
	useEffect(() => {
		if (!hasMore || isLoadingMore || !customers || customers.length === 0 || !loadMoreCustomers) return

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
					console.log('🔄 Carregando mais clientes via scroll infinito...')
					loadMoreCustomers()
				}
			},
			{ threshold: 0.1 }
		)

		if (loadingRef.current) {
			observer.observe(loadingRef.current)
		}

		return () => observer.disconnect()
	}, [hasMore, isLoadingMore, loadMoreCustomers, customers])

	const handleActionSelection = (actionType) => {
		if (actionType === 'add') {
			handleStartAddClient()
			return
		}

		if (
			actionType === 'startOrder' &&
			clientSelected &&
			(!clientSelected.idTabelaPrecoVigencia || clientSelected.idTabelaPrecoVigencia === '')
		) {
			toast.info('Cliente sem tabela de preço vigente.')
			return
		}

		if (clientSelected?.flagSituacao === true) {
			toast.error('Cliente bloqueado para novas vendas.')
			return
		}

		selectFunctionToActions(actionType)
	}

	// Determina o texto do contador
	const getCounterText = () => {
		if (!customers || customers.length === 0) return ''

		if (totalRecords > 0) {

			return totalRecords === 1
				? `${totalRecords} cliente encontrado`
				: `${totalRecords} clientes encontrados`
		}
	}
	return (
		<div className="mb-2 min-h-screen">
			<CustomerSearchInputComponent onSubmitRequest={handleSearchClient} />

			{/* Estado inicial - sem busca realizada */}
			{!isLoading && customers === undefined && (
				<div className="w-full flex items-center justify-center">
					<p className="text-center mt-36 text-gray-500">
						Lista de clientes está vazia, realize uma pesquisa.
					</p>
				</div>
			)}

			{/* Nenhum resultado encontrado */}
			{!isLoading && customers && customers.length === 0 && (
				<div className="w-full flex flex-col items-center justify-center mt-20">
					<UserX className="size-16 text-gray-400 mb-4" />
					<h3 className="text-lg font-semibold text-gray-600 mb-2">
						Nenhum cliente encontrado
					</h3>
					<p className="text-center text-gray-500 px-4 mb-6">
						Não há clientes para os critérios de busca selecionados.
						Tente ajustar os filtros ou adicionar um novo cliente.
					</p>
				</div>
			)}

			{/* Resultados encontrados */}
			{!isLoading && customers && customers.length > 0 && (
				<>
					{/* Header com contador */}
					<div className="flex items-center justify-between mt-4 mb-3 px-2">
						<p className="text-sm text-gray-600 flex items-center gap-2">
							<Users className="h-4 w-4" />
							{getCounterText()}

						</p>
					</div>

					{/* Grid de clientes */}
					<div className="grid md:grid-cols-3 grid-cols-1 gap-2 h-full">
						{customers.map((entity, index) => (
							<CustomerCardComponent
								key={`${entity?.idEntidade}-${index}`}
								clientSelected={clientSelected}
								entity={entity}
								onSelectEntity={handleSelectClientToStartActions}
								handleStartView={handleStartViewClient}
							/>
						))}
					</div>

					{/* Loading indicator para scroll infinito */}
					{hasMore && (
						<div
							ref={loadingRef}
							className="flex items-center justify-center py-6 mt-4"
						>
							{isLoadingMore ? (
								<div className="flex items-center gap-2">
									<Loader2 size={20} className="animate-spin text-primary" />
									<span className="text-sm text-gray-600">
										Carregando mais clientes...
									</span>
								</div>
							) : (
								<div className="text-sm text-gray-400">
									Role para baixo para carregar mais
								</div>
							)}
						</div>
					)}

					{/* Indicador de fim da lista */}
					{!hasMore && totalRecords > 0 && customers.length >= 10 && (
						<div className="flex items-center justify-center py-6 mt-4 border-t border-gray-200">
							<div className="text-center">
								<div className="text-sm text-gray-500 mb-1">
									✅ Todos os clientes foram carregados
								</div>
								<div className="text-xs text-gray-400">
									{totalRecords} {totalRecords === 1 ? 'cliente' : 'clientes'} no total
								</div>
							</div>
						</div>
					)}
				</>
			)}

			{/* FAB Menu - Cliente selecionado */}
			{clientSelected ? (
				<FabComponent
					className='bottom-20 md:bottom-4'
					items={MENU_ACTIONS
						.filter(item => {
							// Filtra o botão de catálogo baseado na empresa
							if (item.type === 'catalogoLink') {
								return showCatalogoLink
							}
							return true
						})
						.map((item) => ({
							id: item.id,
							icon: item.icon,
							type: item.type,
							label: item.label,
							disabled:
								(item.type === 'delete' && isDisabledDelete) ||
								(item.type === 'startEdit' && isDisabledEdit) ||
								(item.type === 'makeCall' && (!clientSelected.fone || !clientSelected.fone.trim())) ||
								(item.type === 'sendEmail' && (!clientSelected.email || !clientSelected.email.trim())),
						}))}
					action={handleActionSelection}
				/>
			) : (
				/* FAB Menu - Adicionar cliente */
				<FabComponent
					bottomMenuOffset={true}
					className='bottom-20 md:bottom-4'
					items={[
						{
							id: '7',
							icon: Plus,
							type: 'add',
							disabled: false,
							label: 'Adicionar',
						},
					]}
					action={handleStartAddClient}
				/>
			)}

			{clientSelected && showCatalogoLink && (
				<CatalogoLinkDialogComponent
					isOpen={catalogoDialogOpen}
					onClose={handleCloseCatalogoDialog}
					clienteId={clientSelected.idEntidade?.toString()}
					clienteName={clientSelected.razaoSocialNome}
					clienteCnpj={clientSelected.cnpjCpf}
					clienteEmail={clientSelected.email}
					clienteFone={clientSelected.fone}
					tabelaPrecoId={clientSelected.fkTabelaPreco}
					vigenciaId={clientSelected.idTabelaPrecoVigencia}
				/>
			)}

			<BottomMenuComponent />
		</div>
	)
}